import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { automationAPI } from '../../services/api';
import { toast } from '../../utils/toast';
import Icon from '../../ui/Icon';

const s = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 16, fontWeight: 800, color: '#1A202C' },
  sub: { fontSize: 12, color: '#718096', marginTop: 4, lineHeight: 1.5 },
  banner: {
    background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10,
    padding: '12px 16px', fontSize: 12, color: '#1E40AF', marginBottom: 16, lineHeight: 1.55,
  },
  btn: (v) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
    background: v === 'primary' ? 'var(--accent)' : v === 'danger' ? '#FEF2F2' : v === 'warn' ? '#FEF3C7' : v === 'success' ? '#DCFCE7' : '#F1F5F9',
    color: v === 'primary' ? '#fff' : v === 'danger' ? '#EF4444' : v === 'warn' ? '#92400E' : v === 'success' ? '#166534' : '#374151',
  }),
  input: {
    width: '100%', padding: '9px 11px', border: '1.5px solid #E2E8F0', borderRadius: 8,
    fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
    background: '#FAFAFA', color: '#1A202C',
  },
  label: {
    display: 'block', fontSize: 11, fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5,
  },
  th: {
    fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase',
    letterSpacing: '0.07em', padding: '10px 14px', borderBottom: '1px solid #f1f5f9',
    background: '#F8FAFF', textAlign: 'left',
  },
  td: {
    fontSize: 13, color: '#374151', padding: '11px 14px',
    borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle',
  },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modal: {
    background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
    maxHeight: '90vh', overflow: 'auto', padding: '28px 28px 24px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
};

function parseLines(text) {
  return String(text || '')
    .split(/[\n,;]+/)
    .map(v => v.trim())
    .filter(Boolean);
}

async function queueAction(action, params, label) {
  const result = await automationAPI.submitForApproval(action, params);
  toast(`${label} queued — ${result.approvalNumber}. Nothing runs until M365 Automation approves.`, 'success');
  return result;
}

async function queueMany(items, buildRequest) {
  const results = [];
  for (const item of items) {
    const { action, params, label } = buildRequest(item);
    results.push(await queueAction(action, params, label));
  }
  return results;
}

function ModalShell({ title, subtitle, onClose, children, footer }) {
  return (
    <div style={s.modalOverlay}>
      <div style={s.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C' }}>{title}</div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <Icon name="close" size={18} color="#9CA3AF" />
          </button>
        </div>
        {subtitle && <p style={{ fontSize: 12, color: '#718096', marginBottom: 20, lineHeight: 1.5 }}>{subtitle}</p>}
        {children}
        {footer}
      </div>
    </div>
  );
}

function UserPicker({ value, onChange, onSelect, disabled, placeholder }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = String(value || '').trim();
    if (q.length < 2) {
      setOptions([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await automationAPI.listUsers({ search: q, top: 15 });
        setOptions(data.users || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={s.input}
        type="text"
        placeholder={placeholder || 'Search M365 user or type email…'}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      />
      {loading && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Searching…</div>}
      {options.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
          marginTop: 4, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)', maxHeight: 180, overflowY: 'auto',
        }}>
          {options.map(u => {
            const email = u.userPrincipalName || u.mail;
            return (
              <button
                key={u.id}
                type="button"
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px',
                  border: 'none', borderBottom: '1px solid #f1f5f9', background: '#fff', cursor: 'pointer',
                }}
                onClick={() => onSelect(email, u)}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>{u.displayName}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>{email}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GroupPicker({ value, onChange, onSelect, disabled }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = String(value || '').trim();
    if (q.length < 2) {
      setOptions([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await automationAPI.listGroups({ search: q, top: 15 });
        setOptions(data.groups || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={s.input}
        placeholder="Search group name or email…"
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      />
      {loading && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Searching…</div>}
      {options.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
          marginTop: 4, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)', maxHeight: 180, overflowY: 'auto',
        }}>
          {options.map(g => (
            <button
              key={g.id}
              type="button"
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px',
                border: 'none', borderBottom: '1px solid #f1f5f9', background: '#fff', cursor: 'pointer', fontSize: 12,
              }}
              onClick={() => onSelect(g.displayName || g.mail, g)}
            >
              {g.displayName}{g.mail ? ` (${g.mail})` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BulkAddMembersModal({ defaultGroup, onClose, onSubmitted }) {
  const [groupKey, setGroupKey] = useState(defaultGroup || '');
  const [emailsText, setEmailsText] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const group = groupKey.trim();
    const emails = parseLines(emailsText);
    if (!group) { toast('Group is required', 'error'); return; }
    if (!emails.length) { toast('Add at least one email', 'error'); return; }
    if (!window.confirm(`Submit ${emails.length} ADD request(s) for group "${group}"?\n\nEach requires approval in M365 Automation.`)) return;
    setBusy(true);
    try {
      await queueMany(emails, email => ({
        action: 'ADD_TO_EMAIL_GROUP',
        params: { groupName: group, groupId: group, memberEmail: email, userKey: email },
        label: `Add ${email}`,
      }));
      toast(`${emails.length} request(s) queued for approval. Nothing runs until M365 Automation approves.`, 'success');
      onSubmitted();
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title="Request bulk add members"
      subtitle="Each email becomes a separate approval request. No members are added until approved in M365 Automation."
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button type="button" style={s.btn()} onClick={onClose}>Cancel</button>
          <button type="button" style={s.btn('primary')} onClick={submit} disabled={busy}>
            {busy ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={s.label}>Group *</label>
          <GroupPicker value={groupKey} onChange={setGroupKey} onSelect={(name) => setGroupKey(name)} disabled={busy} />
        </div>
        <div>
          <label style={s.label}>Member emails (one per line) *</label>
          <textarea
            style={{ ...s.input, minHeight: 120, resize: 'vertical' }}
            placeholder={'john.tan@company.com\njane.lee@company.com'}
            value={emailsText}
            onChange={e => setEmailsText(e.target.value)}
          />
        </div>
      </div>
    </ModalShell>
  );
}

function AddUserToGroupsModal({ onClose, onSubmitted }) {
  const [userEmail, setUserEmail] = useState('');
  const [groupsText, setGroupsText] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const email = userEmail.trim().toLowerCase();
    const groups = parseLines(groupsText);
    if (!email) { toast('User email is required', 'error'); return; }
    if (!groups.length) { toast('Add at least one group', 'error'); return; }
    if (!window.confirm(`Submit ${groups.length} ADD request(s) for ${email}?\n\nEach requires approval.`)) return;
    setBusy(true);
    try {
      await queueMany(groups, group => ({
        action: 'ADD_TO_EMAIL_GROUP',
        params: { groupName: group, groupId: group, memberEmail: email, userKey: email },
        label: `Add to ${group}`,
      }));
      toast(`${groups.length} request(s) queued for approval. Nothing runs until M365 Automation approves.`, 'success');
      onSubmitted();
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title="Request add user to groups"
      subtitle="Each group becomes a separate approval request. Nothing changes in M365 until approved."
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button type="button" style={s.btn()} onClick={onClose}>Cancel</button>
          <button type="button" style={s.btn('primary')} onClick={submit} disabled={busy}>
            {busy ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={s.label}>User *</label>
          <UserPicker value={userEmail} onChange={setUserEmail} onSelect={(email) => setUserEmail(email)} disabled={busy} />
        </div>
        <div>
          <label style={s.label}>Groups (one per line) *</label>
          <textarea
            style={{ ...s.input, minHeight: 120, resize: 'vertical' }}
            placeholder={'Finance-DL\nAll Staff\nVPN-Users'}
            value={groupsText}
            onChange={e => setGroupsText(e.target.value)}
          />
        </div>
      </div>
    </ModalShell>
  );
}

function OnboardToGroupModal({ defaultGroup, licenses, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    displayName: '', userPrincipalName: '', jobTitle: '', department: '',
    licenseSkuId: '', grantVpn: false, welcomeEmailTo: '', sendWelcome: true,
  });
  const [busy, setBusy] = useState(false);
  const groupName = defaultGroup || '';

  const submit = async () => {
    if (!form.displayName.trim() || !form.userPrincipalName.trim()) {
      toast('Full name and company email are required', 'error');
      return;
    }
    if (!groupName.trim()) {
      toast('Open a group first or use Bulk Add', 'error');
      return;
    }
    if (!window.confirm(`Submit ONBOARD for ${form.userPrincipalName} into "${groupName}"?\n\nRequires approval.`)) return;
    setBusy(true);
    try {
      await queueAction('ONBOARD_USER', {
        displayName: form.displayName.trim(),
        userPrincipalName: form.userPrincipalName.trim().toLowerCase(),
        jobTitle: form.jobTitle.trim() || undefined,
        department: form.department.trim() || undefined,
        licenseSkuId: form.licenseSkuId || undefined,
        emailGroups: [groupName.trim()],
        grantVpn: form.grantVpn,
        welcomeEmailTo: form.welcomeEmailTo.trim() || undefined,
        sendWelcome: form.sendWelcome,
      }, 'Onboard user');
      onSubmitted();
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, opts = {}) => (
    <div>
      <label style={s.label}>{label}{opts.required ? ' *' : ''}</label>
      <input
        type={opts.type || 'text'}
        style={s.input}
        value={form[key]}
        placeholder={opts.placeholder}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      />
    </div>
  );

  return (
    <ModalShell
      title="Request onboard to group"
      subtitle={groupName ? `Approval request only. New user will join "${groupName}" after M365 Automation approves.` : 'Select a group from the list first.'}
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button type="button" style={s.btn()} onClick={onClose}>Cancel</button>
          <button type="button" style={s.btn('primary')} onClick={submit} disabled={busy || !groupName}>
            {busy ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {field('displayName', 'Full name', { required: true })}
        {field('userPrincipalName', 'Company email', { required: true, type: 'email' })}
        {field('jobTitle', 'Job title')}
        {field('department', 'Department')}
        <div>
          <label style={s.label}>License</label>
          <select style={s.input} value={form.licenseSkuId} onChange={e => setForm(f => ({ ...f, licenseSkuId: e.target.value }))}>
            <option value="">Default / none</option>
            {(licenses || []).map(l => (
              <option key={l.skuId} value={l.skuId}>{l.skuPartNumber}</option>
            ))}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.grantVpn} onChange={e => setForm(f => ({ ...f, grantVpn: e.target.checked }))} />
          Grant VPN access
        </label>
        {field('welcomeEmailTo', 'Welcome email to', { type: 'email' })}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.sendWelcome} onChange={e => setForm(f => ({ ...f, sendWelcome: e.target.checked }))} />
          Send welcome email after approval
        </label>
      </div>
    </ModalShell>
  );
}

function MemberActions({
  member, groupKey, licenses, busy, setBusy, onUserUpdated,
}) {
  const email = member?.userPrincipalName || member?.mail;
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [otherGroupSearch, setOtherGroupSearch] = useState('');
  const [otherGroupOptions, setOtherGroupOptions] = useState([]);
  const [selectedLicense, setSelectedLicense] = useState('');

  useEffect(() => {
    if (!email) return;
    setDetailLoading(true);
    setDetail(null);
    setOtherGroupSearch('');
    setOtherGroupOptions([]);
    setSelectedLicense('');
    automationAPI.getUser(email)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [email]);

  useEffect(() => {
    if (!otherGroupSearch.trim() || otherGroupSearch.length < 2) {
      setOtherGroupOptions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const data = await automationAPI.listGroups({ search: otherGroupSearch.trim(), top: 15 });
        setOtherGroupOptions(data.groups || []);
      } catch {
        setOtherGroupOptions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [otherGroupSearch]);

  const runAction = async (action, params, label) => {
    if (!window.confirm(`Submit "${label}" for ${email}?\n\nRequires approval in M365 Automation.`)) return;
    setBusy(true);
    try {
      await queueAction(action, params, label);
      onUserUpdated?.();
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const addToOtherGroup = async (group) => {
    const gname = group.displayName || group.mail;
    if (!window.confirm(`Submit ADD ${email} to "${gname}"?\n\nRequires approval.`)) return;
    setBusy(true);
    try {
      await queueAction('ADD_TO_EMAIL_GROUP', {
        groupName: gname, groupId: gname, memberEmail: email, userKey: email,
      }, 'Add to group');
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeFromGroup = async (group) => {
    const gname = group.displayName || group.mail;
    if (!window.confirm(`Submit REMOVE ${email} from "${gname}"?\n\nRequires approval.`)) return;
    setBusy(true);
    try {
      await queueAction('REMOVE_FROM_EMAIL_GROUP', {
        groupName: gname, groupId: gname, memberEmail: email, userKey: email,
      }, 'Remove from group');
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!member) return null;

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #E2E8F0' }}>
      <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4 }}>{member.displayName || email}</div>
      <div style={{ fontSize: 11, color: '#718096', marginBottom: 12 }}>{email}</div>

      {detailLoading ? (
        <div style={{ fontSize: 12, color: '#718096' }}>Loading user…</div>
      ) : detail ? (
        <div style={{ fontSize: 12, marginBottom: 12 }}>
          <div><strong>Job:</strong> {detail.user.jobTitle || '—'}</div>
          <div><strong>Dept:</strong> {detail.user.department || '—'}</div>
          {detail.groups?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>OTHER GROUPS</div>
              <div style={{ maxHeight: 80, overflowY: 'auto' }}>
                {detail.groups.filter(g => (g.displayName || g.mail) !== groupKey).slice(0, 8).map(g => (
                  <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 6, fontSize: 11, marginBottom: 3 }}>
                    <span>{g.displayName}</span>
                    <button type="button" disabled={busy} style={{ ...s.btn('danger'), padding: '2px 6px', fontSize: 10 }} onClick={() => removeFromGroup(g)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      <div style={{ marginBottom: 12 }}>
        <label style={s.label}>Add to another group</label>
        <input style={s.input} placeholder="Search group…" value={otherGroupSearch} onChange={e => setOtherGroupSearch(e.target.value)} />
        {otherGroupOptions.map(g => (
          <button
            key={g.id}
            type="button"
            disabled={busy}
            style={{
              display: 'block', width: '100%', textAlign: 'left', marginTop: 4, padding: '8px 10px',
              border: '1px solid #E2E8F0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 11,
            }}
            onClick={() => addToOtherGroup(g)}
          >
            {g.displayName}
          </button>
        ))}
      </div>

      {licenses.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <label style={s.label}>Assign license</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <select style={{ ...s.input, flex: 1 }} value={selectedLicense} onChange={e => setSelectedLicense(e.target.value)}>
              <option value="">Select…</option>
              {licenses.map(l => <option key={l.skuId} value={l.skuId}>{l.skuPartNumber}</option>)}
            </select>
            <button
              type="button"
              disabled={busy || !selectedLicense}
              style={s.btn('warn')}
              onClick={() => runAction('ASSIGN_LICENSE', { userKey: email, skuId: selectedLicense }, 'Assign license')}
            >
              Request
            </button>
          </div>
        </div>
      )}

      <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', marginBottom: 6, textTransform: 'uppercase' }}>User actions</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button type="button" disabled={busy} style={s.btn('warn')} onClick={() => runAction('RESET_PASSWORD', { userKey: email, email }, 'Password reset')}>
          Password reset
        </button>
        <button type="button" disabled={busy} style={s.btn('warn')} onClick={() => runAction('ENABLE_VPN_ACCESS', { userKey: email, email }, 'VPN access')}>
          VPN access
        </button>
        <button type="button" disabled={busy} style={s.btn('warn')} onClick={() => runAction('DISABLE_VPN_ACCESS', { userKey: email, email }, 'Remove VPN')}>
          Remove VPN
        </button>
        <button type="button" disabled={busy} style={s.btn('warn')} onClick={() => runAction('REVOKE_SESSIONS', { userKey: email }, 'Revoke sessions')}>
          Revoke sign-in sessions
        </button>
        <button type="button" disabled={busy} style={s.btn('warn')} onClick={() => runAction('DISABLE_USER', { userKey: email }, 'Disable account')}>
          Disable account
        </button>
        <button type="button" disabled={busy} style={s.btn('danger')} onClick={() => runAction('OFFBOARD_USER', { userKey: email, email }, 'Offboard user')}>
          Offboard user
        </button>
        {groupKey && (
          <button
            type="button"
            disabled={busy}
            style={s.btn('danger')}
            onClick={() => runAction('REMOVE_FROM_EMAIL_GROUP', {
              groupName: groupKey, groupId: groupKey, memberEmail: email, userKey: email,
            }, `Remove from ${groupKey}`)}
          >
            Remove from this group
          </button>
        )}
      </div>
    </div>
  );
}

function ScopeBanner({ scope }) {
  if (!scope?.enforced) return null;
  return (
    <div style={s.banner}>
      <strong>Scoped automation active.</strong>
      {' '}Only these groups can be used: {(scope.allowedGroups || []).join(', ')}.
      <br />
      <strong>Allowed users:</strong> {(scope.allowedUsers || []).join(', ')}
    </div>
  );
}

export default function M365GroupsAdmin() {
  const [search, setSearch] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [groupMeta, setGroupMeta] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [memberFilter, setMemberFilter] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [scope, setScope] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await automationAPI.listGroups({ search: search.trim() || undefined, top: 100 });
      setGroups(data.groups || []);
      if (data.scope) setScope(data.scope);
    } catch (err) {
      toast(err.message || 'Failed to load groups', 'error');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    automationAPI.listLicenses().then(d => setLicenses(d.licenses || [])).catch(() => {});
  }, []);

  const openGroup = async (group) => {
    setSelected(group);
    setSelectedMember(null);
    setMembersLoading(true);
    setAddEmail('');
    setBulkEmails('');
    setMemberFilter('');
    try {
      const key = group.displayName || group.mail || group.id;
      const data = await automationAPI.getGroupMembers(key);
      setGroupMeta(data.group || group);
      setMembers(data.members || []);
    } catch (err) {
      toast(err.message || 'Failed to load members', 'error');
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const groupKey = selected?.displayName || selected?.mail || selected?.id;

  const filteredMembers = useMemo(() => {
    const q = memberFilter.trim().toLowerCase();
    if (!q) return members;
    return members.filter(m => {
      const email = (m.userPrincipalName || m.mail || '').toLowerCase();
      const name = (m.displayName || '').toLowerCase();
      return email.includes(q) || name.includes(q);
    });
  }, [members, memberFilter]);

  const submitAdd = async () => {
    const email = addEmail.trim();
    if (!email) { toast('Member email is required', 'error'); return; }
    if (!window.confirm(`Submit ADD ${email} to ${groupKey}?\n\nRequires approval.`)) return;
    setBusy(true);
    try {
      await queueAction('ADD_TO_EMAIL_GROUP', {
        groupName: groupKey, groupId: groupKey, memberEmail: email, userKey: email,
      }, 'Add to group');
      setAddEmail('');
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const submitBulkAdd = async () => {
    const emails = parseLines(bulkEmails);
    if (!emails.length) { toast('Add at least one email', 'error'); return; }
    if (!window.confirm(`Submit ${emails.length} ADD request(s) for ${groupKey}?\n\nRequires approval.`)) return;
    setBusy(true);
    try {
      await queueMany(emails, email => ({
        action: 'ADD_TO_EMAIL_GROUP',
        params: { groupName: groupKey, groupId: groupKey, memberEmail: email, userKey: email },
        label: `Add ${email}`,
      }));
      toast(`${emails.length} request(s) submitted`, 'success');
      setBulkEmails('');
    } catch (err) {
      toast(err.message || 'Request failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={s.header}>
        <div>
          <div style={s.title}>M365 Groups</div>
          <div style={s.sub}>Manage group membership and user actions — same approval safety as M365 Users.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={s.btn('success')} onClick={() => setModal('bulk')}>
            <Icon name="plus" size={14} color="#166534" /> Request bulk add
          </button>
          <button type="button" style={s.btn('primary')} onClick={() => setModal('userToGroups')}>
            <Icon name="plus" size={14} color="#fff" /> Request user → groups
          </button>
          <button
            type="button"
            style={s.btn('warn')}
            onClick={() => setModal('onboard')}
            disabled={!selected}
            title={selected ? 'Request onboard new user into selected group' : 'Select a group first'}
          >
            Request onboard to group
          </button>
          <button type="button" style={s.btn()} onClick={load} disabled={loading}>
            <Icon name="refresh" size={14} color="#374151" /> Refresh
          </button>
        </div>
      </div>

      <ScopeBanner scope={scope} />
      <div style={s.banner}>
        <strong>Production safety:</strong> Add/remove members, onboard, password reset, VPN, disable — <strong>only submit requests here</strong>.
        Microsoft 365 is changed <strong>only after approval</strong> in M365 Automation.
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          style={{ ...s.input, minWidth: 220, width: 'auto' }}
          placeholder="Search group name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <button type="button" style={s.btn('primary')} onClick={load}>Search</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 440px' : '1fr', gap: 16 }}>
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>Loading…</div>
          ) : groups.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>No groups found.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead>
                  <tr>
                    <th style={s.th}>Display name</th>
                    <th style={s.th}>Email</th>
                    <th style={s.th}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map(g => (
                    <tr
                      key={g.id}
                      style={{ cursor: 'pointer', background: selected?.id === g.id ? '#EFF6FF' : 'transparent' }}
                      onClick={() => openGroup(g)}
                    >
                      <td style={{ ...s.td, fontWeight: 600 }}>{g.displayName || '—'}</td>
                      <td style={s.td}>{g.mail || '—'}</td>
                      <td style={s.td}>{g.mailEnabled ? 'Mail' : g.securityEnabled ? 'Security' : 'Group'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selected && (
          <div style={{
            background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 18,
            maxHeight: 'calc(100vh - 220px)', overflowY: 'auto',
          }}>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>{groupMeta?.displayName || selected.displayName}</div>
            <div style={{ fontSize: 12, color: '#718096', marginBottom: 12 }}>{groupMeta?.mail || selected.mail || '—'}</div>
            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 16 }}>
              {groupMeta?.mailEnabled ? 'Mail-enabled' : ''}{groupMeta?.securityEnabled ? ' · Security group' : ''}
              {members.length ? ` · ${members.length} member(s)` : ''}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={s.label}>Add one member</label>
              <UserPicker value={addEmail} onChange={setAddEmail} onSelect={(email) => setAddEmail(email)} disabled={busy} />
              <button type="button" disabled={busy || !addEmail.trim()} style={{ ...s.btn('success'), marginTop: 8, width: '100%' }} onClick={submitAdd}>
                Submit add request
              </button>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={s.label}>Bulk add (one email per line)</label>
              <textarea
                style={{ ...s.input, minHeight: 72, resize: 'vertical' }}
                placeholder={'user1@company.com\nuser2@company.com'}
                value={bulkEmails}
                onChange={e => setBulkEmails(e.target.value)}
              />
              <button type="button" disabled={busy || !bulkEmails.trim()} style={{ ...s.btn('success'), marginTop: 8, width: '100%' }} onClick={submitBulkAdd}>
                Submit bulk add
              </button>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={s.label}>Members {membersLoading ? '…' : `(${filteredMembers.length})`}</label>
              <input
                style={s.input}
                placeholder="Filter members…"
                value={memberFilter}
                onChange={e => setMemberFilter(e.target.value)}
              />
            </div>

            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: 8, marginBottom: 8 }}>
              {membersLoading ? (
                <div style={{ padding: 16, color: '#718096', fontSize: 13 }}>Loading…</div>
              ) : filteredMembers.length === 0 ? (
                <div style={{ padding: 16, color: '#718096', fontSize: 13 }}>No members.</div>
              ) : (
                filteredMembers.map(m => {
                  const active = selectedMember?.id === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px',
                        border: 'none', borderBottom: '1px solid #f1f5f9',
                        background: active ? '#EFF6FF' : '#fff', cursor: 'pointer',
                      }}
                      onClick={() => setSelectedMember(m)}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{m.displayName || '—'}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{m.userPrincipalName || m.mail}</div>
                    </button>
                  );
                })
              )}
            </div>

            <MemberActions
              member={selectedMember}
              groupKey={groupKey}
              licenses={licenses}
              busy={busy}
              setBusy={setBusy}
            />

            <button
              type="button"
              style={{ ...s.btn(), marginTop: 12, width: '100%' }}
              onClick={() => { setSelected(null); setMembers([]); setSelectedMember(null); }}
            >
              Close group
            </button>
          </div>
        )}
      </div>

      {modal === 'bulk' && (
        <BulkAddMembersModal
          defaultGroup={groupKey}
          onClose={() => setModal(null)}
          onSubmitted={() => setModal(null)}
        />
      )}
      {modal === 'userToGroups' && (
        <AddUserToGroupsModal onClose={() => setModal(null)} onSubmitted={() => setModal(null)} />
      )}
      {modal === 'onboard' && (
        <OnboardToGroupModal
          defaultGroup={groupKey}
          licenses={licenses}
          onClose={() => setModal(null)}
          onSubmitted={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
